import { useEffect, useState } from 'preact/hooks';
import { NPCS } from '../../content/npcs';
import {
  type GearItem,
  ITEMS,
  RARITY_COLOR,
  SET_NAMES,
  makeGear,
  rarityIndex,
} from '../../game/items';
import { ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { scaleCost } from './bag';
import {
  type SellRow,
  type ShopDef,
  type ShopEntry,
  blockReason,
  buyFromShop,
  entryName,
  entryRarity,
  remainingFor,
  resolveShop,
  sellRow,
  sellableRows,
  shopGroup,
} from './shops';
import {
  Amount,
  Cell,
  Coin,
  Empty,
  Price,
  Stepper,
  TipLayer,
  Wallet,
  commit,
  compact,
  confirmThen,
  toast,
  useCtx,
} from './ui';

/** Gear entries are previewed as a real (unsaved) item so the bag art and tooltip apply. */
const previews = new Map<string, GearItem>();
function previewGear(shop: ShopDef, e: ShopEntry): GearItem | null {
  if (!e.gear) return null;
  const key = `${shop.id}:${e.id}:${e.gear.ilvl}`;
  let g = previews.get(key);
  if (!g) {
    g = makeGear(e.gear.slot, e.gear.ilvl, e.gear.rarity, {
      set: e.gear.set,
      quality: e.gear.quality ?? 60,
    });
    previews.set(key, g);
  }
  return g;
}

const PER_LABEL = { day: 'today', week: 'this week', ever: 'total' } as const;

function entrySub(e: ShopEntry): string {
  if (e.gear)
    return `Item level ${e.gear.ilvl}${e.gear.set ? ` · ${SET_NAMES[e.gear.set] ?? e.gear.set} set` : ''}${e.gear.quality === undefined ? ' · random quality' : ''}`;
  const def = e.item ? ITEMS[e.item] : undefined;
  return def?.description ?? '';
}

/** A shop's wares with a purchase bar. `rep` is the Bounty standing (seal exchange); `repName` labels its tiers. */
export function ShopView({
  shop,
  rep = 0,
  repName = (t: number) => `standing ${t}`,
}: {
  shop: ShopDef;
  rep?: number;
  repName?: (tier: number) => string;
}) {
  const ctx = useCtx();
  const [sel, setSel] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  useEffect(() => {
    setSel(null);
    setMsg(null);
  }, [shop.id]);
  if (!ctx) return null;
  const { p, c, a } = ctx;
  const entries = shop.entries(c);
  if (!entries.length)
    return <Empty title="Nothing for sale">This merchant has no wares for you yet.</Empty>;
  const cur = entries.find((e) => e.id === sel) ?? null;
  const left = cur ? remainingFor(c, a, shop, cur) : Infinity;
  const maxQty = cur ? Math.max(1, Math.min(99, Number.isFinite(left) ? left : 99)) : 1;
  const q = Math.min(qty, maxQty);
  const block = cur ? blockReason(p, shop, cur, q, rep) : null;

  const buy = () => {
    if (!cur) return;
    const r = buyFromShop(p, shop, cur.id, q, rep);
    if (!r.ok) {
      setMsg({ text: r.reason, ok: false });
      return;
    }
    const n = (cur.count ?? 1) * q;
    setMsg({ text: `Purchased ${entryName(cur)}${n > 1 ? ` ×${n}` : ''}`, ok: true });
    toast(
      `Purchased <span style="color:${RARITY_COLOR[entryRarity(cur)]}">${entryName(cur)}</span>${n > 1 ? ` ×${n}` : ''}`,
      RARITY_COLOR[entryRarity(cur)],
    );
    commit();
  };

  return (
    <div class="shop-view">
      <div class="shop-list fx-scroll">
        {entries.map((e) => {
          const lock =
            (e.minLevel && c.level < e.minLevel ? `Requires level ${e.minLevel}` : null) ??
            (e.minRep && rep < e.minRep ? `Requires ${repName(e.minRep)}` : null);
          const rem = remainingFor(c, a, shop, e);
          const gear = previewGear(shop, e);
          return (
            <div
              key={e.id}
              class={`fx-row shop-row${sel === e.id ? ' is-sel' : ''}${lock ? ' is-locked' : ''}`}
              onClick={() => {
                setSel(e.id);
                setQty(1);
                setMsg(null);
              }}
              onDblClick={() => {
                setSel(e.id);
                setQty(1);
                if (!blockReason(p, shop, e, 1, rep)) {
                  const r = buyFromShop(p, shop, e.id, 1, rep);
                  if (r.ok) {
                    toast(`Purchased ${entryName(e)}`, RARITY_COLOR[entryRarity(e)]);
                    commit();
                  }
                }
              }}
            >
              <Cell item={gear ?? e.item ?? ''} count={e.count} size={42} dim={!!lock} />
              <div class="shop-namecol">
                <div
                  class="fx-name"
                  style={{ color: lock ? undefined : RARITY_COLOR[entryRarity(e)] }}
                >
                  {entryName(e)}
                  {(e.count ?? 1) > 1 && <span class="shop-count"> ×{e.count}</span>}
                </div>
                <div class={`fx-sub${lock ? ' shop-lock' : ''}`}>{lock ?? entrySub(e)}</div>
              </div>
              <div class="shop-limit">
                {e.limit ? (
                  <span
                    class={rem <= 0 ? 'is-out' : ''}
                  >{`${rem}/${e.limit.n} ${PER_LABEL[e.limit.per]}`}</span>
                ) : null}
              </div>
              <Price cost={e.price} c={c} a={a} />
            </div>
          );
        })}
      </div>
      <div class={`shop-buybar${cur ? '' : ' is-idle'}`}>
        {cur ? (
          <>
            <Cell
              item={previewGear(shop, cur) ?? cur.item ?? ''}
              count={(cur.count ?? 1) * q}
              size={46}
            />
            <div class="shop-buy-name">
              <div class="fx-name" style={{ color: RARITY_COLOR[entryRarity(cur)] }}>
                {entryName(cur)}
              </div>
              <div class={`shop-msg${msg ? (msg.ok ? ' is-ok' : ' is-err') : ''}`}>
                {msg?.text ?? block ?? `${(cur.count ?? 1) * q} total`}
              </div>
            </div>
            <Stepper value={q} max={maxQty} onChange={setQty} />
            <div class="shop-total">
              <span class="fx-h">Total</span>
              <Price cost={scaleCost(cur.price, q)} c={c} a={a} />
            </div>
            <button class="btn btn--gold shop-buy-btn" disabled={!!block} onClick={buy}>
              Buy
            </button>
          </>
        ) : (
          <span class="fx-dim">Select an item to buy. Double-click buys one.</span>
        )}
      </div>
    </div>
  );
}

function SellView() {
  const ctx = useCtx();
  const [sel, setSel] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  if (!ctx) return null;
  const { p, c } = ctx;
  const rows = sellableRows(c).sort(
    (x, y) =>
      Number(x.kind === 'stack') - Number(y.kind === 'stack') ||
      rarityIndex(y.rarity) - rarityIndex(x.rarity) ||
      x.name.localeCompare(y.name),
  );
  if (!rows.length)
    return (
      <Empty title="Nothing to sell">
        Equipped gear, tokens and quest items are never offered to merchants.
      </Empty>
    );
  const cur = rows.find((r) => r.key === sel) ?? null;
  const q = cur ? Math.min(qty, cur.count) : 1;
  const itemOf = (r: SellRow) =>
    (r.kind === 'equip' ? c.inventory.items.find((i) => i.uid === r.id) : undefined) ?? r.id;

  const doSell = (r: SellRow, n: number) => {
    const run = () => {
      const got = sellRow(p, r, n);
      if (got > 0) {
        toast(
          `Sold ${r.name}${n > 1 ? ` ×${n}` : ''} for ${got.toLocaleString('en-US')} silver`,
          '#c9d0d7',
        );
        setSel(null);
        commit();
      }
    };
    if (r.kind === 'equip' && rarityIndex(r.rarity) >= rarityIndex('epic'))
      confirmThen(`Sell ${r.name}? Merchants don't sell items back.`, 'Sell', run);
    else run();
  };

  return (
    <div class="shop-view">
      <div class="shop-list fx-scroll">
        {rows.map((r) => (
          <div
            key={r.key}
            class={`fx-row shop-row shop-row--sell${sel === r.key ? ' is-sel' : ''}`}
            onClick={() => {
              setSel(r.key);
              setQty(r.count);
            }}
          >
            <Cell item={itemOf(r)} count={r.count} size={42} />
            <div class="shop-namecol">
              <div class="fx-name" style={{ color: RARITY_COLOR[r.rarity] }}>
                {r.name}
              </div>
              <div class="fx-sub">
                {r.kind === 'equip' ? 'Equipment' : `${compact(r.count)} in bag`}
              </div>
            </div>
            <span class="shop-each">
              <Amount kind="silver" n={r.unit} />
              {r.count > 1 && <small>each</small>}
            </span>
          </div>
        ))}
      </div>
      <div class={`shop-buybar${cur ? '' : ' is-idle'}`}>
        {cur ? (
          <>
            <Cell item={itemOf(cur)} count={q} size={46} />
            <div class="shop-buy-name">
              <div class="fx-name" style={{ color: RARITY_COLOR[cur.rarity] }}>
                {cur.name}
              </div>
              <div class="shop-msg">No buyback — sales are final.</div>
            </div>
            {cur.count > 1 ? <Stepper value={q} max={cur.count} onChange={setQty} /> : <span />}
            <div class="shop-total">
              <span class="fx-h">You receive</span>
              <Amount kind="silver" n={cur.unit * q} />
            </div>
            <button class="btn btn--gold shop-buy-btn" onClick={() => doSell(cur, q)}>
              Sell
            </button>
          </>
        ) : (
          <span class="fx-dim">Select something from your bag to sell.</span>
        )}
      </div>
    </div>
  );
}

/** Panel `shop`: panelArg is an NPC id (its `shop`) or a shop id. */
export function ShopPanel() {
  const ctx = useCtx();
  const arg = ui.panelArg.value;
  const home = resolveShop(arg);
  const npc = arg ? NPCS[arg] : undefined;
  const [tab, setTab] = useState<string>(home?.id ?? 'sell');
  useEffect(() => setTab(home?.id ?? 'sell'), [arg]);
  if (!ctx) return null;
  const group = home ? shopGroup(home) : [];
  const shop = group.find((s) => s.id === tab) ?? home;
  const exchange = !!home?.group;
  const title = exchange
    ? 'Endgame Exchange'
    : (home?.name ?? (npc ? `${npc.name}'s Wares` : 'Merchant'));
  const vendor = home ? home.vendor : true;
  const tokens = shop?.tokens ?? [];
  return (
    <Window
      title={title}
      width="min(800px, 96vw)"
      height="min(640px, 88vh)"
      className="fx-win fx-shop"
    >
      <div class="fx-bar-top">
        <div class="fx-tabs" role="tablist">
          {home &&
            group.map((s) => (
              <button
                key={s.id}
                class="tab"
                role="tab"
                aria-selected={tab === s.id}
                onClick={() => setTab(s.id)}
              >
                {exchange && s.tokens?.[0] ? <Coin kind={s.tokens[0]} size={12} /> : null}
                {exchange ? s.name : 'Wares'}
              </button>
            ))}
          {vendor && (
            <button
              class="tab"
              role="tab"
              aria-selected={tab === 'sell'}
              onClick={() => setTab('sell')}
            >
              Sell
            </button>
          )}
        </div>
        <span class="fx-spacer" />
        <Wallet a={ctx.a} c={ctx.c} tokens={tab === 'sell' ? [] : tokens} />
      </div>
      <div class="shop-blurb">
        {npc && (
          <span class="shop-role">
            {npc.name} · {npc.role}
          </span>
        )}
        <span>
          {tab === 'sell'
            ? 'Merchants pay the listed value in silver. There is no buyback.'
            : (shop?.blurb ?? npc?.greeting ?? '')}
        </span>
      </div>
      {tab === 'sell' || !shop ? <SellView /> : <ShopView shop={shop} />}
      <TipLayer />
    </Window>
  );
}
